import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { LogoMark } from "./Logo";
import { MagneticButton } from "./MagneticButton";

const PARTICLES = Array.from({ length: 20 }, (_, i) => ({
  left: `${(i * 43 + 11) % 100}%`,
  top: `${(i * 31 + 9) % 100}%`,
  delay: `${(i % 8) * 0.7}s`,
  size: (i % 3) + 1,
}));

const CORE_SEQUENCE = ["logo", "/images/work-1.webp", "/images/work-4.webp", "/images/work-6.webp"];

const HeroCore = () => {
  const [core, setCore] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCore((c) => (c + 1) % CORE_SEQUENCE.length), 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative w-40 h-40 sm:w-48 sm:h-48" data-testid="hero-core">
      <motion.div
        aria-hidden="true"
        className="absolute -inset-10 rounded-full bg-[#6D00FF]/25 blur-[60px]"
        animate={{ opacity: [0.4, 0.8, 0.4] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute -inset-4 rounded-full border border-dashed border-[#8B2CFF]/35"
        animate={{ rotate: 360 }}
        transition={{ duration: 36, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute -inset-9"
        animate={{ rotate: -360 }}
        transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
      >
        <span className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#C43CFF] shadow-[0_0_14px_rgba(196,60,255,0.95)]" />
      </motion.div>
      <motion.div
        aria-hidden="true"
        className="absolute -inset-9"
        animate={{ rotate: 360 }}
        transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
      >
        <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E8B6FF] shadow-[0_0_10px_rgba(232,182,255,0.9)]" />
      </motion.div>

      <div className="absolute inset-0 rounded-full overflow-hidden border border-[#8B2CFF]/30 bg-[#0a0a10]">
        {CORE_SEQUENCE.map((item, i) => (
          <motion.div
            key={i}
            className="absolute inset-0 flex items-center justify-center"
            animate={{ opacity: core === i ? 1 : 0, scale: core === i ? 1 : 1.12 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            {item === "logo" ? (
              <LogoMark className="w-16 h-16 sm:w-20 sm:h-20 drop-shadow-[0_0_24px_rgba(139,44,255,0.8)]" />
            ) : (
              <>
                <img src={item} alt="BrandZenith AI-generated creative concept visual" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-[#6D00FF]/20 mix-blend-overlay" aria-hidden="true" />
                <div
                  className="absolute inset-0 bg-gradient-to-t from-[#050507]/70 via-transparent to-transparent"
                  aria-hidden="true"
                />
              </>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const MaskedLine = ({ text, index, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.55 + index * 0.15, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const scrollDown = () => {
    const el = document.getElementById("services");
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg" aria-hidden="true" />
      <div className="absolute inset-0 noise-overlay opacity-[0.05] pointer-events-none" aria-hidden="true" />

      <motion.div
        aria-hidden="true"
        className="absolute -top-48 left-1/2 -translate-x-1/2 w-[min(90vw,900px)] h-[520px] rounded-full bg-[#6D00FF]/20 blur-[140px]"
        animate={{ opacity: [0.55, 0.85, 0.55], scale: [1, 1.1, 1] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute bottom-[-20%] right-[-10%] w-[min(70vw,640px)] h-[420px] rounded-full bg-[#C43CFF]/10 blur-[160px]"
        animate={{ opacity: [0.4, 0.7, 0.4], x: [0, -40, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute top-1/3 left-[-30%] w-[60%] h-px bg-gradient-to-r from-transparent via-[#8B2CFF]/50 to-transparent"
        animate={{ x: ["0%", "160%"] }}
        transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute top-2/3 left-[-30%] w-[50%] h-px bg-gradient-to-r from-transparent via-[#C43CFF]/40 to-transparent"
        animate={{ x: ["0%", "190%"] }}
        transition={{ duration: 24, repeat: Infinity, ease: "linear", delay: 6 }}
      />

      {PARTICLES.map((p, i) => (
        <span
          key={i}
          aria-hidden="true"
          className="absolute rounded-full bg-[#C43CFF] animate-twinkle"
          style={{ left: p.left, top: p.top, width: p.size, height: p.size, animationDelay: p.delay }}
        />
      ))}

      <motion.div
        style={{ y, opacity }}
        className="relative z-10 flex flex-col items-center text-center px-6 pt-24"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.7, filter: "blur(8px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="mb-10"
        >
          <HeroCore />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.34em] text-[#A6A6B0] mb-7"
        >
          AI-Powered <span className="text-[#C43CFF]">Brand Growth</span> Studio
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-display font-extrabold uppercase tracking-tighter leading-[0.95] text-[13vw] sm:text-7xl lg:text-8xl"
        >
          <MaskedLine text="MAKE YOUR" index={0} />
          <MaskedLine
            text="NEXT MOVE."
            index={1}
            className="text-gradient-purple drop-shadow-[0_0_36px_rgba(139,44,255,0.35)]"
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-8 max-w-xl text-sm sm:text-lg text-[#A6A6B0] leading-relaxed"
        >
          Strategy, creativity and AI working together to build brands that move forward.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.35 }}
          className="mt-11 flex flex-col sm:flex-row items-center gap-4"
        >
          <MagneticButton
            data-testid="hero-primary-cta"
            onClick={() => navigate("/contact")}
            className="group flex items-center gap-3 bg-[#6D00FF] hover:bg-[#8B2CFF] text-white text-sm font-semibold uppercase tracking-[0.12em] px-8 py-4 rounded-md transition-[background-color,box-shadow] duration-300 hover:shadow-[0_0_44px_-8px_rgba(139,44,255,0.8)]"
          >
            Request a Proposal
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </MagneticButton>
          <button
            data-testid="hero-secondary-cta"
            onClick={() => navigate("/services")}
            className="text-sm font-semibold uppercase tracking-[0.12em] px-8 py-4 rounded-md border border-white/15 text-white/90 hover:border-[#8B2CFF]/60 hover:text-[#E8B6FF] transition-[border-color,color,box-shadow] duration-300 hover:shadow-[0_0_30px_-10px_rgba(139,44,255,0.5)]"
          >
            Explore Services
          </button>
        </motion.div>

        <motion.div
          data-testid="hero-mini-flow"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.9, duration: 1.2 }}
          className="mt-16 flex items-center gap-3 sm:gap-5"
        >
          {["IDEA", "AI", "CREATIVE", "GROWTH"].map((s, i) => (
            <span key={s} className="flex items-center gap-3 sm:gap-5">
              <span
                className={`text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.3em] ${
                  s === "AI" ? "text-[#C43CFF]/80" : "text-white/35"
                }`}
              >
                {s}
              </span>
              {i < 3 && (
                <span className="relative w-8 sm:w-14 h-px bg-white/10 overflow-hidden">
                  <motion.span
                    className="absolute inset-y-0 w-3 bg-gradient-to-r from-transparent via-[#C43CFF] to-transparent"
                    animate={{ x: ["-12px", "60px"] }}
                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.5, ease: "linear" }}
                  />
                </span>
              )}
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={scrollDown}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#A6A6B0] hover:text-white transition-colors duration-300"
        aria-label="Scroll down"
      >
        <motion.span
          className="block"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="w-5 h-5" />
        </motion.span>
      </motion.button>

      <div
        aria-hidden="true"
        className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#050507] to-transparent"
      />
    </section>
  );
};
